(function () {
  const playVideo = (swiper) => {
    if (!swiper?.enabled) return;
    const prevSlide = swiper.slides[swiper.previousIndex];
    const activeSlide = swiper.slides[swiper.activeIndex];

    if (activeSlide) {
      // Shopify hosted videos
      activeSlide
        .querySelectorAll("video.slideshow-slide__video")
        .forEach((video) => {
          video.play().catch((error) => {
            console.error("Error playing video:", error);
          });
        });

      // Embedded videos (Youtube or Vimeo)
      activeSlide
        .querySelectorAll("iframe.slideshow-slide__video")
        .forEach((iframe) => {
          if (iframe.classList.contains("js-youtube")) {
            iframe.contentWindow.postMessage(
              '{"event":"command","func":"playVideo","args":""}',
              "*"
            );
          } else if (iframe.classList.contains("js-vimeo")) {
            iframe.contentWindow.postMessage('{"method":"play"}', "*");
          }
        });
    }

    if (prevSlide && prevSlide !== activeSlide) {
      // Shopify hosted videos
      prevSlide
        .querySelectorAll("video.slideshow-slide__video")
        .forEach((video) => {
          video.pause();
        });

      // Embedded videos (Youtube or Vimeo)
      prevSlide
        .querySelectorAll("iframe.slideshow-slide__video")
        .forEach((iframe) => {
          if (iframe.classList.contains("js-youtube")) {
            iframe.contentWindow.postMessage(
              '{"event":"command","func":"pauseVideo","args":""}',
              "*"
            );
          } else if (iframe.classList.contains("js-vimeo")) {
            iframe.contentWindow.postMessage('{"method":"pause"}', "*");
          }
        });
    }
  };

  const stopVideo = (swiper) => {
    if (!swiper?.enabled) return;
    const activeSlide = swiper.slides[swiper.activeIndex];

    if (activeSlide) {
      // Shopify hosted videos
      activeSlide
        .querySelectorAll("video.slideshow-slide__video")
        .forEach((video) => {
          video.pause();
        });

      // Embedded videos (Youtube or Vimeo)
      activeSlide
        .querySelectorAll("iframe.slideshow-slide__video")
        .forEach((iframe) => {
          if (iframe.classList.contains("js-youtube")) {
            iframe.contentWindow.postMessage(
              '{"event":"command","func":"pauseVideo","args":""}',
              "*"
            );
          } else if (iframe.classList.contains("js-vimeo")) {
            iframe.contentWindow.postMessage('{"method":"pause"}', "*");
          }
        });
    }
  };

  const initAutoplayObserver = (section, sliderEl) => {
    if (!section || !section?.classList.contains("slideshow-section"))
      return null;
    if (!sliderEl || !sliderEl.swiper) return null;

    const hasAutoplay = sliderEl.getAttribute("data-autoplay") === "true";

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (hasAutoplay) sliderEl.swiper.autoplay.resume();
          playVideo(sliderEl.swiper);
        } else {
          if (hasAutoplay) sliderEl.swiper.autoplay.pause();
          stopVideo(sliderEl.swiper);
        }
      });
    });

    sectionObserver.observe(section);
    return sectionObserver;
  };

  // The controls bar is an absolute overlay, so it cannot push the copy off itself in
  // flow: the copy reserves room for it instead. Publish the bar's measured height so
  // the reserve follows what is actually there — a bullet row that wraps onto a second
  // or third line lifts the copy by exactly what it grew (see controls-reserve() in
  // section-slideshow.scss, which falls back to the unwrapped height until this lands).
  const initControlsReserve = (section) => {
    const wrapper = section.querySelector(".slideshow-wrapper");
    const controls = section.querySelector(".slideshow-controls");
    if (!wrapper || !controls) return null;

    const apply = () => {
      const { height } = controls.getBoundingClientRect();
      wrapper.style.setProperty("--slideshow-controls-height", `${height}px`);
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(controls);

    return () => {
      observer.disconnect();
      wrapper.style.removeProperty("--slideshow-controls-height");
    };
  };

  // Stored cleanup callbacks keyed by section id, so shopify:section:unload can
  // tear down the cursor-nav listeners and the IntersectionObserver (rule D).
  const cleanups = {};

  const runCleanup = (sectionId) => {
    const fn = cleanups[sectionId];
    if (typeof fn === "function") {
      fn();
      delete cleanups[sectionId];
    }
  };

  // Swaps ONE line of the preview panel in two steps: the line on screen drops out
  // through the bottom of its own mask, then the next slide's version rises back
  // through it. Each line runs its own sequence off its own animationend, so the
  // caption and the name below it stay independent — CSS staggers them (see
  // --slideshow-line-delay). A `text` of null re-plays the motion without changing
  // the wording, which is what the caption does. The text is replaced between the
  // two steps, so the leaving line keeps its own. A timer is the safety net for a
  // card that never animates (hidden, or a browser that skips it).
  const lineSwaps = new WeakMap();

  // The hidden copy of the name each label is measured against (see
  // initPreviewLabelReserve).
  const labelMeasures = new WeakMap();

  // Sizes the name's mask to the name that is about to arrive. The two can take a
  // different number of lines, and the column is centred, so without this the caption
  // above jumps the frame the text is replaced; with the height set up front the box
  // travels while the name is out of the mask (the transition lives on .is-resizing,
  // so a swap that runs without motion still snaps).
  const sizeLabelMask = (labelEl, text, animate) => {
    const maskEl = labelEl.parentElement;
    const measureEl = labelMeasures.get(labelEl);
    if (!maskEl || !measureEl) return;

    measureEl.textContent = text;
    maskEl.classList.toggle("is-resizing", Boolean(animate));
    const { height } = measureEl.getBoundingClientRect();
    if (!height) return;
    maskEl.style.setProperty("--slideshow-preview-label-height", `${height}px`);
  };

  // The measuring copy is cloned from the visible line, so it wraps and clamps
  // exactly like it. A narrower card rewraps the name, hence the observer.
  const initPreviewLabelReserve = (previewEl) => {
    const labelEl = previewEl.querySelector(".js-slideshow-preview-label");
    const maskEl = labelEl ? labelEl.parentElement : null;
    if (!labelEl || !maskEl) return null;

    const measureEl = labelEl.cloneNode(false);
    measureEl.classList.remove("js-slideshow-preview-label");
    measureEl.classList.add("slideshow-preview__label-measure");
    measureEl.setAttribute("aria-hidden", "true");
    maskEl.append(measureEl);
    labelMeasures.set(labelEl, measureEl);

    const apply = () => {
      const { height } = measureEl.getBoundingClientRect();
      if (!height) return;
      maskEl.style.setProperty(
        "--slideshow-preview-label-height",
        `${height}px`
      );
    };
    // Seeded with whatever the line already says, so the box starts on a measured
    // height: a swap out of `auto` cannot be animated, and the first one would snap.
    measureEl.textContent = labelEl.textContent;
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(measureEl);

    return () => {
      observer.disconnect();
      labelMeasures.delete(labelEl);
      measureEl.remove();
      maskEl.classList.remove("is-resizing");
      maskEl.style.removeProperty("--slideshow-preview-label-height");
    };
  };

  const swapPreviewLine = (lineEl, text, animate) => {
    if (!animate) {
      if (text !== null) lineEl.textContent = text;
      return;
    }

    // A swap still in flight is abandoned — its listener and timer see a newer
    // token here and stop, so only the latest change writes the text.
    const running = lineSwaps.get(lineEl);
    if (running) clearTimeout(running.timer);
    const swap = {};
    lineSwaps.set(lineEl, swap);

    lineEl.classList.remove("is-entering", "is-leaving");
    // Restart the keyframe even when a swap is already running.
    void lineEl.offsetWidth;
    lineEl.classList.add("is-leaving");

    const enter = () => {
      if (lineSwaps.get(lineEl) !== swap || swap.done) return;
      swap.done = true;
      clearTimeout(swap.timer);
      if (text !== null) lineEl.textContent = text;
      lineEl.classList.remove("is-leaving");
      void lineEl.offsetWidth;
      lineEl.classList.add("is-entering");
      lineEl.addEventListener(
        "animationend",
        () => lineEl.classList.remove("is-entering"),
        { once: true }
      );
    };

    lineEl.addEventListener("animationend", enter, { once: true });
    // The net waits out whatever CSS just set — the line's own delay included — so it
    // never cuts the step short.
    const style = getComputedStyle(lineEl);
    const leaveMs =
      (parseFloat(style.animationDuration) + parseFloat(style.animationDelay)) *
      1000;
    swap.timer = setTimeout(enter, leaveMs + 100);
  };

  // Fade and creative stack their slides, so Swiper's own drag is off for them: with
  // nothing to follow, a slow drag either scrubs through several slides or snaps back
  // and the slider reads as dead. The gesture is recognised here instead — one swipe,
  // or one trackpad flick, advances exactly one slide whatever its speed.
  const initGestureNavigation = (sliderEl, swiper) => {
    const SWIPE_DISTANCE = 40;
    const WHEEL_DISTANCE = 40;
    const GESTURE_COOLDOWN = 900;

    let startX = 0;
    let startY = 0;
    let tracking = false;
    let lastGesture = 0;

    const advance = (direction) => {
      const now = Date.now();
      if (now - lastGesture < GESTURE_COOLDOWN) return false;
      lastGesture = now;
      if (direction > 0) {
        swiper.slideNext();
      } else {
        swiper.slidePrev();
      }
      return true;
    };

    const onPointerDown = (event) => {
      tracking = true;
      startX = event.clientX;
      startY = event.clientY;
    };

    const onPointerUp = (event) => {
      if (!tracking) return;
      tracking = false;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (Math.abs(dx) < SWIPE_DISTANCE || Math.abs(dx) <= Math.abs(dy)) return;
      advance(dx < 0 ? 1 : -1);
    };

    const onWheel = (event) => {
      // Vertical scrolling belongs to the page; only a sideways flick counts.
      if (Math.abs(event.deltaX) < WHEEL_DISTANCE) return;
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      if (advance(event.deltaX > 0 ? 1 : -1)) event.preventDefault();
    };

    sliderEl.addEventListener("pointerdown", onPointerDown);
    sliderEl.addEventListener("pointerup", onPointerUp);
    sliderEl.addEventListener("pointercancel", () => {
      tracking = false;
    });
    sliderEl.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      sliderEl.removeEventListener("pointerdown", onPointerDown);
      sliderEl.removeEventListener("pointerup", onPointerUp);
      sliderEl.removeEventListener("wheel", onWheel);
    };
  };

  // A floating arrow that follows the cursor inside the slider and switches the
  // slide on click (far half = next, near half = prev with a flipped arrow).
  const initCursorNavigation = (section, sliderEl, swiper) => {
    const cursorEl = section.querySelector(".js-slideshow-cursor");
    if (!cursorEl) return null;

    // Read off the tracked element rather than the document, as the shared
    // SliderDragCursor does: it is the direction Swiper itself resolves.
    let isRtl = getComputedStyle(sliderEl).direction === "rtl";

    // In RTL the slides read right to left, so the near half is the NEXT one —
    // the shared cursor CSS flips the glyphs on that assumption.
    const isPrevHalf = (x, width) => (isRtl ? x > width / 2 : x < width / 2);

    // The half the pointer last sat on, so the edge state can be refreshed when the
    // slide moves under a cursor that is standing still (autoplay, a bullet, a drag).
    let isPrevSide = false;

    // The arrow carries the same disabled state the nav buttons get from Swiper: on a
    // non-looping edge it points nowhere, so it dims instead of reading as live.
    const syncEdgeState = () => {
      const isDisabled = swiper.params.loop
        ? false
        : Boolean(isPrevSide ? swiper.isBeginning : swiper.isEnd);
      cursorEl.classList.toggle("slider-drag-cursor--disabled", isDisabled);
      return isDisabled;
    };

    const setSide = (isPrev) => {
      isPrevSide = isPrev;
      cursorEl.classList.toggle("slider-drag-cursor--prev", isPrev);
      cursorEl.classList.toggle("slider-drag-cursor--next", !isPrev);
      syncEdgeState();
    };

    const onMove = (event) => {
      const rect = sliderEl.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      cursorEl.style.setProperty("--cursor-x", `${x}px`);
      cursorEl.style.setProperty("--cursor-y", `${y}px`);
      setSide(isPrevHalf(x, rect.width));
    };

    const onEnter = () => {
      isRtl = getComputedStyle(sliderEl).direction === "rtl";
      cursorEl.classList.add("is-visible");
      syncEdgeState();
    };
    const onLeave = () => cursorEl.classList.remove("is-visible");

    // Swiper recomputes isBeginning/isEnd right before it emits progress, so this is
    // the one hook that covers every way the slide can change.
    const onProgress = () => syncEdgeState();

    const onClick = (event) => {
      // Ignore clicks on interactive content (buttons, links, controls).
      if (
        event.target.closest("a, button, .button, input, .slider-nav-button")
      ) {
        return;
      }
      const rect = sliderEl.getBoundingClientRect();
      setSide(isPrevHalf(event.clientX - rect.left, rect.width));

      // A disabled arrow does nothing at all — not even the click pulse.
      if (syncEdgeState()) return;

      cursorEl.classList.add("is-clicking");
      cursorEl.addEventListener(
        "animationend",
        () => cursorEl.classList.remove("is-clicking"),
        { once: true }
      );

      if (isPrevSide) swiper.slidePrev();
      else swiper.slideNext();
    };

    sliderEl.addEventListener("pointermove", onMove);
    sliderEl.addEventListener("pointerenter", onEnter);
    sliderEl.addEventListener("pointerleave", onLeave);
    sliderEl.addEventListener("click", onClick);
    swiper.on("progress", onProgress);

    return () => {
      sliderEl.removeEventListener("pointermove", onMove);
      sliderEl.removeEventListener("pointerenter", onEnter);
      sliderEl.removeEventListener("pointerleave", onLeave);
      sliderEl.removeEventListener("click", onClick);
      if (typeof swiper.off === "function") swiper.off("progress", onProgress);
      cursorEl.classList.remove("slider-drag-cursor--disabled");
    };
  };

  // The media of every slide after the first is parked in a <template> by
  // _slideshow-slide.liquid, so it doesn't compete with the first slide while the
  // page loads. Swap it in once the page has loaded, or earlier if the shopper
  // reaches for the slideshow or a short autoplay moves on — whichever comes first.
  const swapInDeferredMedia = (section) => {
    section
      .querySelectorAll("template.js-slideshow-deferred-media")
      .forEach((template) => template.replaceWith(template.content));
  };

  const loadDeferredMedia = (section) => {
    if (window.Shopify?.designMode || document.readyState === "complete") {
      swapInDeferredMedia(section);
      return;
    }

    const swapIn = () => swapInDeferredMedia(section);
    window.addEventListener("load", swapIn, { once: true });
    ["pointerenter", "focusin"].forEach((type) =>
      section.addEventListener(type, swapIn, { once: true })
    );
  };

  const initSlideshow = (section) => {
    if (!section || !section?.classList.contains("slideshow-section")) return;

    const sliderEl = section.querySelector(".js-slideshow-swiper");
    if (!sliderEl) return;

    loadDeferredMedia(section);

    runCleanup(section.id);
    if (sliderEl.swiper) sliderEl.swiper.destroy(true, true);

    const animationType = sliderEl.dataset?.animationType || "slide";
    // Fade and creative keep their slides stacked — nothing moves under the finger.
    const isStackedEffect =
      animationType === "fade" || animationType === "creative";
    const paginationEl = section.querySelector(".slider-pagination");

    // Autoplay fill of the active bullet. Swiper rearms its timer on every pause it
    // takes — the slide transition, hover, the section leaving and re-entering the
    // viewport — and each rearm counts a full delay again. Fed straight to the bullet
    // that rewinds it mid-slide; clamped, it stalls until the timer catches up. So a
    // rearm keeps the fill where it is and spreads what is LEFT of the bar over what
    // is left of the timer: the bar only moves forward, at a steady pace, and still
    // lands on full exactly when the slide flips. Reset happens in one place — the
    // slide change.
    let bulletFill = 0;
    let bulletFillBase = 0;
    let lastTimerFraction = 0;
    const setBulletProgress = (value) => {
      if (!paginationEl) return;
      // Dropping the variable hands the bullet back to its CSS default — a full fill,
      // which is what a slider without autoplay shows.
      if (value === null) {
        bulletFill = 0;
        bulletFillBase = 0;
        lastTimerFraction = 0;
        paginationEl.style.removeProperty("--slider-progress");
        return;
      }
      if (value < bulletFill) return;
      bulletFill = value;
      paginationEl.style.setProperty("--slider-progress", value);
    };
    const trackBulletProgress = (timerFraction) => {
      if (timerFraction < lastTimerFraction) bulletFillBase = bulletFill;
      lastTimerFraction = timerFraction;
      setBulletProgress(bulletFillBase + (1 - bulletFillBase) * timerFraction);
    };
    const resetBulletProgress = () => {
      if (!paginationEl) return;
      bulletFill = 0;
      bulletFillBase = 0;
      lastTimerFraction = 0;
      paginationEl.style.setProperty("--slider-progress", 0);
    };

    const nextBtn = section.querySelector(".slider-nav-button--next");
    const prevBtn = section.querySelector(".slider-nav-button--prev");
    const previewEl = section.querySelector(".js-slideshow-preview");
    const cursorNavigation =
      sliderEl.getAttribute("data-cursor-navigation") === "true";
    const sliderLoop = sliderEl.getAttribute("data-loop") === "true";
    const hasSliderAutoplay = sliderEl.getAttribute("data-autoplay") === "true";
    const pauseAutoplayOnHover =
      sliderEl.getAttribute("data-autoplay-pause-on-hover") === "true";
    const autoplayDelay = Number(sliderEl.dataset.autoplayDelay || 5000);

    // Only an autoplaying slider keeps the played bullets filled (the row then reads
    // as a progress track); without autoplay the fill marks the active bullet alone.
    if (paginationEl) {
      paginationEl.classList.toggle(
        "slider-pagination--autoplay",
        hasSliderAutoplay
      );
    }

    const slides = sliderEl.querySelectorAll(
      ".swiper-slide:not(.swiper-slide-duplicate)"
    );

    const paginationConfig = {
      el: paginationEl,
      clickable: true,
    };

    // Slides off the stage keep their buttons in the document: without this Tab
    // walks into a slide nobody can see, and the browser scrolling that button
    // into view drags the slider onto it — the copy changes under a keyboard user
    // who was only on their way to the controls. inert takes the idle slides out
    // of the tab order and off the accessibility tree in one attribute.
    const syncSlidesAccessibility = (swiper) => {
      // By index rather than the active class: this runs on slideChange, before
      // Swiper has necessarily rewritten the classes.
      const activeSlide = swiper.slides[swiper.activeIndex];
      swiper.slides.forEach((slide) => {
        const isActive = slide === activeSlide;
        slide.toggleAttribute("inert", !isActive);
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");
      });
    };

    // Set up before the first fill below, so even the instant one already sizes the
    // name's mask to its own text.
    const labelReserveCleanup = previewEl
      ? initPreviewLabelReserve(previewEl)
      : null;

    // The slide the panel currently shows. A looping slider re-seats its slides on
    // init and on every resize, and each loopFix fires slideChange with the same
    // slide still up next — without this the panel would replay its swap on a plain
    // page load or window resize.
    let previewIndex = null;

    // Fills the detailed-pagination preview panel with the NEXT slide's
    // thumbnail + label, so the merchant/customer previews where a click leads.
    const updatePreview = (swiper, animate) => {
      if (!previewEl || !slides.length) return;
      const total = slides.length;
      const nextIndex = (swiper.realIndex + 1) % total;
      const nextSlide = slides[nextIndex];
      if (!nextSlide) return;
      if (nextIndex === previewIndex) return;
      // The first fill is instant — the panel has nothing to swap out yet, and a
      // looping slider can reach here before its own init event through loopFix.
      // The sliding effect keeps the panel still: only the stacked effects, where
      // the slide itself changes in place, animate the thumbnail and the label.
      const withMotion =
        animate && previewIndex !== null && animationType !== "slide";
      previewIndex = nextIndex;

      const captionEl = previewEl.querySelector(
        ".js-slideshow-preview-caption"
      );
      const labelEl = previewEl.querySelector(".js-slideshow-preview-label");
      const thumbEl = previewEl.querySelector(".js-slideshow-preview-thumb");

      if (labelEl) {
        const label = nextSlide.getAttribute("data-bullet-label") || "";
        sizeLabelMask(labelEl, label, withMotion);
        swapPreviewLine(labelEl, label, withMotion);
      }
      // The caption keeps its wording — it swaps only to move with the name.
      if (captionEl) swapPreviewLine(captionEl, null, withMotion);
      if (thumbEl) {
        // Clone the next slide's thumb source (an <img> or its placeholder SVG)
        // so a slide with no media shows the SAME placeholder in the preview.
        const tpl = nextSlide.querySelector(".js-slideshow-slide-thumb");
        if (!tpl || !tpl.content) return;

        if (!withMotion) {
          thumbEl.replaceChildren(tpl.content.cloneNode(true));
          return;
        }

        // Same reveal as the hero: the new thumbnail is masked open over the outgoing
        // one, started on the slide change itself and run for the slider's own
        // transition time (CSS reads it from --slideshow-preview-swap-duration), so
        // the small and the big image change together.
        const outgoing = Array.from(thumbEl.children);
        thumbEl.append(tpl.content.cloneNode(true));
        const revealEl = thumbEl.lastElementChild;
        if (!revealEl) return;

        revealEl.classList.add("is-revealing");
        let settled = false;
        const settle = () => {
          if (settled) return;
          settled = true;
          outgoing.forEach((el) => el.remove());
          revealEl.classList.remove("is-revealing");
        };
        revealEl.addEventListener("animationend", settle, { once: true });
        // An animation that never runs (a hidden card) must not leave the old
        // thumbnail stacked under the new one.
        setTimeout(settle, swiper.params.speed + 200);
      }
    };

    // Snapshot of the z-index Swiper assigned to the slides we lift during a
    // creative transition, so transitionEnd can put it back (see below).
    let creativeZRestore = null;

    const swiperInstance = new Swiper(sliderEl, {
      loop: sliderLoop,
      slidesPerView: 1,
      // No gutter at any width: the contained hero changes slides edge to edge,
      // exactly like the full-bleed one.
      spaceBetween: 0,
      speed: animationType === "creative" ? 1000 : 800,
      watchSlideProgress: true,
      // Only the sliding effect has something to follow under the finger; the stacked
      // ones hand the gesture to initGestureNavigation above (see the note there).
      allowTouchMove: !isStackedEffect,
      effect: animationType,
      creativeEffect:
        animationType === "creative"
          ? {
              // Slides stay stacked (no movement); the incoming image is masked
              // open over the outgoing slide via CSS (.is-revealing).
              // The ~imperceptible 0.999 opacity is load-bearing: a virtual-
              // translate effect ends the transition off the slide's own
              // transitionend, and with a pure [0,0,0]/opacity:1 nothing on the
              // slide changes, so it never fires and Swiper stays locked forever.
              prev: { translate: [0, 0, 0], opacity: 0.999 },
              next: { translate: [0, 0, 0], opacity: 0.999 },
            }
          : {},
      resistanceRatio: animationType === "creative" ? 0 : 0.85,
      mousewheel: {
        forceToAxis: true,
      },
      autoplay: hasSliderAutoplay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: pauseAutoplayOnHover,
          }
        : false,
      pagination: paginationConfig,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
        disabledClass: "swiper-button-disabled",
      },
      on: {
        init() {
          if (!hasSliderAutoplay) setBulletProgress(null);
          // Everything the panel animates on a slide change — the thumbnail reveal
          // and the label swap — is timed off the slider itself, so the small and
          // the big change land on the same frame whatever the speed.
          if (previewEl) {
            previewEl.style.setProperty(
              "--slideshow-preview-swap-duration",
              `${this.params.speed}ms`
            );
          }
          updatePreview(this);
          syncSlidesAccessibility(this);
        },
        autoplayTimeLeft(s, time, progress) {
          trackBulletProgress(1 - progress);
        },
        slideChange() {
          // The fill belongs to the slide that is counting down, so it starts from
          // zero here and nowhere else.
          if (hasSliderAutoplay) resetBulletProgress();
          updatePreview(this, true);
          syncSlidesAccessibility(this);
        },
        slideChangeTransitionStart() {
          swapInDeferredMedia(section);
          playVideo(this);

          if (animationType === "creative") {
            // Lock advancing for the duration of the zoom so a rapid click can't
            // interrupt and restart it mid-grow.
            this.allowSlideNext = false;
            this.allowSlidePrev = false;

            // Grow the incoming slide's image from a small centred box; the class
            // re-add restarts the keyframe on every change.
            const activeSlide = this.slides[this.activeIndex];
            const prevSlide = this.slides[this.previousIndex];

            // Lift the incoming slide above all and the OUTGOING one directly
            // beneath it, so the growing image reveals the slide we just left —
            // the stacked creative effect gives prev + next an EQUAL z-index, so
            // DOM order would otherwise float the upcoming slide on top. Snapshot
            // Swiper's own z first: every slide carries will-change:transform (its
            // own layer), so clearing z to auto on transitionEnd would let DOM
            // order paint the LAST slide over the active one at rest — restore it.
            creativeZRestore = [];
            const topZ = this.slides.length + 2;
            if (activeSlide) {
              creativeZRestore.push([activeSlide, activeSlide.style.zIndex]);
              activeSlide.classList.add("is-revealing");
              activeSlide.style.zIndex = topZ;
            }
            if (prevSlide && prevSlide !== activeSlide) {
              creativeZRestore.push([prevSlide, prevSlide.style.zIndex]);
              prevSlide.style.zIndex = topZ - 1;
            }
          }
        },
        slideChangeTransitionEnd() {
          if (animationType === "creative") {
            this.allowSlideNext = true;
            this.allowSlidePrev = true;

            this.slides.forEach((slide) =>
              slide.classList.remove("is-revealing")
            );

            // Restore Swiper's z-index (it keeps the active slide on top). Do NOT
            // clear to auto — with will-change layers the last DOM slide would
            // paint over the active one at rest.
            if (creativeZRestore) {
              creativeZRestore.forEach(([slide, z]) => {
                slide.style.zIndex = z;
              });
              creativeZRestore = null;
            }
          }
        },
      },
    });

    const observer = initAutoplayObserver(section, sliderEl);
    const reserveCleanup = initControlsReserve(section);
    const cursorCleanup = cursorNavigation
      ? initCursorNavigation(section, sliderEl, swiperInstance)
      : null;
    const gestureCleanup = isStackedEffect
      ? initGestureNavigation(sliderEl, swiperInstance)
      : null;

    // The whole detailed-pagination preview card (thumbnail + caption/label)
    // advances the slider on click, so clicking the next-slide thumbnail jumps
    // to THAT (next) slide.
    let onPreviewClick = null;
    let onPreviewProgress = null;
    const previewNextBtn = previewEl
      ? previewEl.querySelector(".js-slideshow-preview-next")
      : null;
    if (previewEl) {
      // The card only ever goes forward, so on a non-looping end it is as dead as the
      // arrows there — disabling its own button both dims it and drops its tab stop.
      const syncPreviewEdge = () => {
        const isDisabled = swiperInstance.params.loop
          ? false
          : Boolean(swiperInstance.isEnd);
        if (previewNextBtn) previewNextBtn.disabled = isDisabled;
        return isDisabled;
      };

      onPreviewProgress = () => syncPreviewEdge();
      swiperInstance.on("progress", onPreviewProgress);
      syncPreviewEdge();

      onPreviewClick = () => {
        // The thumbnail sits outside the button, so the card needs the guard too.
        if (syncPreviewEdge()) return;
        swiperInstance.slideNext();
      };
      previewEl.addEventListener("click", onPreviewClick);
    }

    cleanups[section.id] = () => {
      if (observer) observer.disconnect();
      if (typeof reserveCleanup === "function") reserveCleanup();
      if (typeof labelReserveCleanup === "function") labelReserveCleanup();
      if (typeof cursorCleanup === "function") cursorCleanup();
      if (typeof gestureCleanup === "function") gestureCleanup();
      if (previewEl && onPreviewClick) {
        previewEl.removeEventListener("click", onPreviewClick);
      }
      if (onPreviewProgress && typeof swiperInstance.off === "function") {
        swiperInstance.off("progress", onPreviewProgress);
      }
      if (previewNextBtn) previewNextBtn.disabled = false;
      // A swap caught mid-flight would otherwise leave its line parked off the mask
      // (animation-fill-mode: both) for the re-init to inherit.
      if (previewEl) {
        previewEl
          .querySelectorAll(
            ".js-slideshow-preview-caption, .js-slideshow-preview-label"
          )
          .forEach((lineEl) => {
            const swap = lineSwaps.get(lineEl);
            if (swap) clearTimeout(swap.timer);
            lineSwaps.delete(lineEl);
            lineEl.classList.remove("is-leaving", "is-entering");
          });
      }
      if (sliderEl.swiper) sliderEl.swiper.destroy(true, true);
      // destroy() drops Swiper's own attributes only — a re-init on a slide left
      // inert would keep it unfocusable for good.
      slides.forEach((slide) => {
        slide.removeAttribute("inert");
        slide.removeAttribute("aria-hidden");
      });
    };
  };

  initSlideshow(document.currentScript.parentElement);

  document.addEventListener("shopify:section:load", function (event) {
    initSlideshow(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    if (!event.target?.classList.contains("slideshow-section")) return;
    runCleanup(event.target.id);
  });
})();
